import React, { useEffect, useState } from 'react';
import {
    Box, Grid, Typography, Table, TableBody, TableCell, TableHead, TableRow, Paper,
    TextField, Button, CircularProgress, Autocomplete, FormControl, InputLabel
} from '@mui/material';
import { useNavigate } from 'react-router';
import Sidebar from './Sidebar';
import axios from '../axiosConfig';

const Limits = () => {
    const navigate = useNavigate();

    // Состояния для данных о лимитах
    const [limits, setLimits] = useState([]);
    const [loading, setLoading] = useState(false);

    // Состояния для отображения формы создания лимита
    const [showAddLimitForm, setShowAddLimitForm] = useState(false);

    // Состояния для добавления лимитов
    const [limitType, setLimitType] = useState('');
    const [asset, setAsset] = useState('');
    const [baseAsset, setBaseAsset] = useState('');
    const [quoteAsset, setQuoteAsset] = useState('');
    const [side, setSide] = useState('');
    const [amount, setAmount] = useState('');

    // Перечисления
    const limitTypes = ['PAYOUT', 'CONVERT'];
    const sides = ['buy', 'sell'];

    // Состояния для активов
    const [assets, setAssets] = useState([]);

    useEffect(() => {
        const token = localStorage.getItem('authToken');

        if (!token || token === 'null') {
            navigate('/login');
        } else {
            fetchLimits();
            fetchAssets(); // Загружаем активы при рендере компонента
        }
    }, [navigate]);

    // Функция для получения списка лимитов
    const fetchLimits = () => {
        setLoading(true);
        axios.get('/limit/list')
            .then((response) => {
                const data = response.data.data || [];
                setLimits(Array.isArray(data) ? data : []);
                setLoading(false);
            })
            .catch((error) => {
                console.error('Ошибка при получении данных лимитов:', error);
                setLoading(false);
            });
    };

    // Функция для получения списка активов (фиатные и криптовалютные активы)
    const fetchAssets = () => {
        const cryptoAssetsEndpoint = '/transaction-info/crypto/assets';
        const fiatAssetsEndpoint = '/transaction-info/fiat/assets';

        Promise.all([
            axios.get(cryptoAssetsEndpoint),
            axios.get(fiatAssetsEndpoint)
        ])
            .then(([cryptoAssetsResponse, fiatAssetsResponse]) => {
                const cryptoAssets = cryptoAssetsResponse.data || [];
                const fiatAssets = fiatAssetsResponse.data || [];
                setAssets([...fiatAssets, ...cryptoAssets]); // Фиатные активы идут первыми
            })
            .catch((error) => {
                console.error('Ошибка при получении списка активов:', error);
            });
    };

    // Функция для добавления лимита
    const addLimit = () => {
        // Проверка на тип лимита
        if (!limitType) {
            alert('Выберите тип лимита.');
            return;
        }

        if (!amount) {
            alert('Введите сумму.');
            return;
        }

        let limitData = {};
        let url = '';

        if (limitType === 'PAYOUT') {
            if (!asset) {
                alert('Выберите актив для выплаты.');
                return;
            }
            limitData = {
                asset,
                amount: Number(amount)
            };
            url = '/limit/payout';
        } else if (limitType === 'CONVERT') {
            if (!baseAsset || !quoteAsset || !side) {
                alert('Пожалуйста, заполните все поля для конвертации.');
                return;
            }
            limitData = {
                base_asset: baseAsset,
                quote_asset: quoteAsset,
                side,
                amount: Number(amount)
            };
            url = '/limit/convert';
        }

        axios.post(url, limitData)
            .then(() => {
                alert('Лимит успешно добавлен');
                resetForm();
                setShowAddLimitForm(false);
                fetchLimits(); // Обновление таблицы после добавления
            })
            .catch((error) => {
                console.error('Ошибка при добавлении лимита:', error);
                alert('Ошибка при добавлении лимита');
            });
    };

    // Функция для удаления лимита
    const deleteLimit = (sk) => {
        if (!window.confirm('Вы действительно хотите удалить этот лимит?')) return;
        const encodedSk = encodeURIComponent(sk);

        axios.delete(`/limit/${encodedSk}`)
            .then(() => {
                alert('Лимит удалён');
                fetchLimits(); // Обновление таблицы после удаления
            })
            .catch((error) => {
                console.error('Ошибка при удалении лимита:', error);
                alert('Ошибка при удалении лимита');
            });
    };

    // Функция для сброса полей формы добавления лимита
    const resetForm = () => {
        setLimitType('');
        setAsset('');
        setBaseAsset('');
        setQuoteAsset('');
        setSide('');
        setAmount('');
    };

    // Функция для отображения полей формы в зависимости от типа лимита
    const renderLimitFormFields = () => {
        if (limitType === 'PAYOUT') {
            return (
                <>
                    <Autocomplete
                        options={assets}
                        getOptionLabel={(option) => option}
                        value={asset}
                        onChange={(event, newValue) => setAsset(newValue)}
                        renderInput={(params) => <TextField {...params} label="Актив" margin="normal" fullWidth />}
                    />

                    <TextField
                        label="Сумма *"
                        variant="outlined"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        type="number"
                        fullWidth
                        margin="normal"
                    />
                </>
            );
        } else if (limitType === 'CONVERT') {
            return (
                <>
                    <Autocomplete
                        options={assets}
                        getOptionLabel={(option) => option}
                        value={baseAsset}
                        onChange={(event, newValue) => setBaseAsset(newValue)}
                        renderInput={(params) => <TextField {...params} label="Базовый актив" margin="normal" fullWidth />}
                    />

                    <Autocomplete
                        options={assets}
                        getOptionLabel={(option) => option}
                        value={quoteAsset}
                        onChange={(event, newValue) => setQuoteAsset(newValue)}
                        renderInput={(params) => <TextField {...params} label="Котируемый актив" margin="normal" fullWidth />}
                    />

                    <Autocomplete
                        options={sides}
                        getOptionLabel={(option) => option}
                        value={side}
                        onChange={(event, newValue) => setSide(newValue)}
                        renderInput={(params) => <TextField {...params} label="Сторона сделки" margin="normal" fullWidth />}
                    />

                    <TextField
                        label="Сумма *"
                        variant="outlined"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        type="number"
                        fullWidth
                        margin="normal"
                    />
                </>
            );
        }
        return null;
    };

    return (
        <Grid container spacing={2}>
            <Grid item xs={4}>
                <Sidebar />
            </Grid>
            <Grid item xs={8}>
                {!showAddLimitForm ? (
                    <>
                        <Box mt={2}>
                            <Typography variant="h4" gutterBottom>Управление Лимитами</Typography>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => setShowAddLimitForm(true)}
                                style={{ marginRight: '10px' }}
                            >
                                Добавить лимит
                            </Button>
                        </Box>

                        {/* Таблица с текущими лимитами */}
                        <Typography variant="h6" mt={4}>Список лимитов</Typography>
                        {loading ? (
                            <CircularProgress />
                        ) : (
                            <Paper style={{ overflowX: 'auto' }}>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Тип лимита</TableCell>
                                            <TableCell>Базовый актив</TableCell>
                                            <TableCell>Котируемый актив</TableCell>
                                            <TableCell>Сторона</TableCell>
                                            <TableCell>Сумма</TableCell>
                                            <TableCell>Действия</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {limits.length > 0 ? (
                                            limits.map((limit, index) => (
                                                <TableRow key={limit.sk || index}>
                                                    <TableCell>{limit.quote_asset ? 'CONVERT' : 'PAYOUT'}</TableCell>
                                                    <TableCell>{limit.base_asset}</TableCell>
                                                    <TableCell>{limit.quote_asset || '-'}</TableCell>
                                                    <TableCell>{limit.side || '-'}</TableCell>
                                                    <TableCell>{limit.amount}</TableCell>
                                                    <TableCell>
                                                        <Button
                                                            variant="contained"
                                                            color="secondary"
                                                            onClick={() => deleteLimit(limit.sk)}
                                                        >
                                                            Удалить
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        ) : (
                                            <TableRow>
                                                <TableCell colSpan={6}>Нет данных для отображения</TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </Paper>
                        )}
                    </>
                ) : (
                    <Box mt={2}>
                        <Button variant="contained" onClick={() => setShowAddLimitForm(false)}>
                            Назад
                        </Button>
                        <Typography variant="h5" mt={2}>Добавить новый лимит</Typography>
                        <FormControl fullWidth margin="normal">
                            <InputLabel>Тип лимита *</InputLabel>
                            <Autocomplete
                                options={limitTypes}
                                getOptionLabel={(option) => option}
                                value={limitType}
                                onChange={(event, newValue) => setLimitType(newValue)}
                                renderInput={(params) => <TextField {...params} label="Тип лимита" margin="normal" fullWidth />}
                            />
                        </FormControl>

                        {renderLimitFormFields()}

                        <Box mt={2}>
                            <Button variant="contained" color="primary" onClick={addLimit}>
                                Добавить лимит
                            </Button>
                        </Box>
                    </Box>
                )}
            </Grid>
        </Grid>
    );
};

export default Limits;
